import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getDashboardStats, getMinhasInscricoes, getAdminDashboardStats } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { LogOut, Settings, Calendar, CheckSquare, UserCheck, ListChecks, Award, Clock, Users, LayoutGrid, List } from 'lucide-react';
import { Skeleton } from '../components/ui/Skeleton';
import InscricaoCard from '../components/InscricaoCard';
import Pagination from '../components/Pagination'; // IMPORTADO

const ITEMS_PER_PAGE = 6; // 6 itens por página no Dashboard

function DashboardPage() {
    const { participanteNome, participanteId, logout, participanteRole } = useAuth();
    const navigate = useNavigate();
    
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    const [allInscricoes, setAllInscricoes] = useState([]); // Todas as inscrições
    const [loadingInscricoes, setLoadingInscricoes] = useState(true);
    const [errorInscricoes, setErrorInscricoes] = useState('');
    
    // Estados de Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    
    // Estado de Visualização e Persistência
    // Mantemos o padrão 'grid' para mobile, mas permitimos que o usuário mude em desktop
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('dashboardInscricoesViewMode') || 'grid'); 

    // Efeito para persistir o viewMode no localStorage
    useEffect(() => {
        localStorage.setItem('dashboardInscricoesViewMode', viewMode);
    }, [viewMode]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!participanteId && participanteRole !== 'ROLE_ADMIN') {
                setLoadingInscricoes(false);
                setLoadingStats(false);
                return;
            }

            try {
                setLoadingStats(true);
                let statsData;
                if (participanteRole === 'ROLE_ADMIN') {
                    statsData = await getAdminDashboardStats();
                } else {
                    statsData = await getDashboardStats(participanteId);
                }
                setStats(statsData);
            } catch (err) {
                console.error('Erro ao buscar estatísticas:', err);
                toast.error('Erro ao carregar estatísticas do dashboard.');
            } finally {
                setLoadingStats(false);
            }

            if (participanteRole !== 'ROLE_ADMIN') {
                try {
                    setLoadingInscricoes(true);
                    setErrorInscricoes('');
                    const inscricoesData = await getMinhasInscricoes(participanteId);
                    
                    // ORDENAÇÃO: Mais recente (maior data) primeiro
                    const sortedInscricoes = inscricoesData.sort((a, b) => {
                        return new Date(b.dataInscricao) - new Date(a.dataInscricao);
                    });

                    setAllInscricoes(sortedInscricoes);
                } catch (err) {
                    console.error('Erro ao buscar minhas inscrições:', err);
                    setErrorInscricoes(err.message);
                    toast.error(err.message);
                } finally {
                    setLoadingInscricoes(false);
                }
            } else {
                setLoadingInscricoes(false);
            }
        };

        fetchDashboardData();
    }, [participanteId, participanteRole]);

    // Lógica de Paginação
    const paginatedInscricoes = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return allInscricoes.slice(startIndex, endIndex);
    }, [allInscricoes, currentPage]);

    useEffect(() => {
        const newTotalPages = Math.ceil(allInscricoes.length / ITEMS_PER_PAGE);
        setTotalPages(newTotalPages > 0 ? newTotalPages : 1);
        // Garante que a página atual é válida
        if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
        } else if (currentPage === 0 && newTotalPages > 0) {
            setCurrentPage(1);
        }
    }, [allInscricoes.length, currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // Opcional: rolar para o topo
    };


    const StatsSkeleton = () => (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(participanteRole === 'ROLE_ADMIN' ? 4 : 3)].map((_, i) => (
                <Card key={i}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <Skeleton className="h-5 w-2/4" />
                        <Skeleton className="h-6 w-6 rounded-sm" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-8 w-1/4" />
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    const InscricoesSkeleton = () => (
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {[...Array(ITEMS_PER_PAGE)].map((_, index) => (
                <Card key={index} className="flex flex-col">
                    <CardHeader><Skeleton className="h-6 w-3/4 mb-2" /></CardHeader>
                    <CardContent className="flex-grow">
                        <Skeleton className="h-4 w-1/2 mb-4" />
                        <div className="flex justify-center bg-muted p-2 rounded-lg shadow-inner mt-auto">
                            <Skeleton className="h-[140px] w-[140px]" />
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">
                        Bem-vindo(a), {participanteNome || 'Participante'}!
                    </h1>
                    <p className="text-muted-foreground mb-4">
                        {participanteRole === 'ROLE_ADMIN'
                            ? 'Aqui pode gerenciar eventos, inscrições e usuários da plataforma.'
                            : 'Aqui pode ver as suas inscrições, certificados e gerir a sua conta.'}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-4 md:mt-0 w-full sm:w-auto">
                    <Link to="/eventos" className="w-full sm:w-auto"><Button variant="outline" className="w-full">Ver Todos os Eventos</Button></Link>
                    <Button onClick={handleLogout} variant="destructive" className="w-full sm:w-auto"><LogOut className="mr-2 h-4 w-4" /> Sair</Button>
                </div>
            </div>

            {/* Painel de Estatísticas */}
            <div className="mb-8">
                {loadingStats ? <StatsSkeleton /> : stats && (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {participanteRole === 'ROLE_ADMIN' ? (
                            <>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Total de Eventos</CardTitle>
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalEventos ?? 0}</div></CardContent>
                                </Card>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Total de Inscrições</CardTitle>
                                        <ListChecks className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalInscricoes ?? 0}</div></CardContent>
                                </Card>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Certificados Emitidos</CardTitle>
                                        <Award className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalCertificadosEmitidos ?? 0}</div></CardContent>
                                </Card>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Total de Participantes</CardTitle>
                                        <Users className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalParticipantes ?? 0}</div></CardContent>
                                </Card>
                            </>
                        ) : (
                            <>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Inscrições Totais</CardTitle>
                                        <ListChecks className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalInscricoes ?? 0}</div></CardContent>
                                </Card>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Certificados Ganhos</CardTitle>
                                        <Award className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalCertificados ?? stats.certificadosGanhos ?? 0}</div></CardContent>
                                </Card>
                                <Card className="transition-all duration-200 ease-in-out hover:shadow-md">
                                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                                        <CardTitle className="text-sm font-medium">Total de Horas</CardTitle>
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent><div className="text-2xl font-bold">{stats.totalHoras ?? 0}h</div></CardContent>
                                </Card>
                            </>
                        )}
                    </div>
                )}
            </div>

            {participanteRole === 'ROLE_ADMIN' && (
                <Card className="mb-8 bg-secondary/50 transition-all duration-200 ease-in-out hover:shadow-md">
                    <CardHeader>
                        <CardTitle className="flex items-center text-lg mb-2"><Settings className="mr-2 h-5 w-5" /> Painel de Administrador</CardTitle>
                        <CardDescription className="leading-relaxed">Acesso rápido às ferramentas de gestão.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        <Link to="/admin/eventos" className="w-full"><Button className="w-full">
                            <Calendar className="mr-2 h-4 w-4" /> Gerenciar Eventos
                        </Button></Link>
                        <Link to="/admin/checkin" className="w-full"><Button className="w-full">
                            <CheckSquare className="mr-2 h-4 w-4" /> Gerenciar Check-in
                        </Button></Link>
                        <Link to="/admin/inscricoes" className="w-full"><Button className="w-full">
                            <UserCheck className="mr-2 h-4 w-4" /> Gerenciar Inscrições
                        </Button></Link>
                        <Link to="/admin/usuarios" className="w-full"><Button className="w-full">
                            <Users className="mr-2 h-4 w-4" /> Gerenciar Usuários
                        </Button></Link>
                    </CardContent>
                </Card>
            )}

            {participanteRole !== 'ROLE_ADMIN' && (
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-semibold">Minhas Inscrições ({allInscricoes.length})</h2>
                        {/* Controles de Visualização - Ocultos em telas pequenas (md:flex) */}
                        <div className="hidden md:flex space-x-2">
                            <Button 
                                variant={viewMode === 'grid' ? 'default' : 'outline'} 
                                size="icon" 
                                onClick={() => setViewMode('grid')}
                                aria-label="Visualização em Grelha"
                            >
                                <LayoutGrid className="h-5 w-5" />
                            </Button>
                            <Button 
                                variant={viewMode === 'list' ? 'default' : 'outline'} 
                                size="icon" 
                                onClick={() => setViewMode('list')}
                                aria-label="Visualização em Lista"
                            >
                                <List className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                    
                    {loadingInscricoes ? (
                        <InscricoesSkeleton />
                    ) : errorInscricoes ? (
                        <p className="text-destructive">{errorInscricoes}</p>
                    ) : allInscricoes.length === 0 ? (
                        <Card className="text-center p-8 border-dashed">
                            <CardContent className="flex flex-col items-center">
                                <p className="text-muted-foreground mb-4">Você ainda não tem nenhuma inscrição.</p>
                                <Link to="/eventos"><Button>Explorar Eventos</Button></Link>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            {/* Em telas pequenas, o grid-cols-1 é forçado, e em md/lg, ele respeita o viewMode */}
                            <div className={`grid gap-6 ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                                {paginatedInscricoes.map(inscricao => (
                                    <InscricaoCard 
                                        key={inscricao.id} 
                                        inscricao={inscricao} 
                                        // Força o modo lista apenas se for desktop E o modo lista estiver ativo
                                        isListMode={viewMode === 'list' && window.innerWidth >= 768} 
                                    />
                                ))}
                            </div>
                            
                            {totalPages > 1 && (
                                <Pagination 
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default DashboardPage;